// apps/web/src/features/admin/automations/create/authoring-options.ts
//
// 4a (E-062) — the create flow's constraint layer, DERIVED from the repaired
// effect registry (P4 "derive, don't duplicate"; R0 made the registry the
// runtime truth and the parity tripwire keeps it that way). Three pure
// selectors feed the three screens:
//
//   whereOptions()            → Screen 1 (WHERE does it fire)
//   scopeOptions(moment)      → Screen 2 (ON WHAT)
//   effectPalette(moment, s)  → Screen 3 THEN (one flat palette)
//
// The E-059 posture is enforced here: disabled options are returned WITH a
// reason, never omitted — the screens render them disabled, never hidden.
import {
  EFFECTS,
  MOMENT,
  MOMENT_PHRASE,
  effectByKey,
  type AutomationBranch,
  type MomentId,
  type ScopeId,
} from '@capture/shared';
import { defaultConfigForEffect } from '../effect-bucket';

export interface WhereOption {
  /** Stable row key — distinct from `moment` for the two batch-flavored rows. */
  key: string;
  /** Canonical moment; null for the permanently-disabled "batch completes" row. */
  moment: MomentId | null;
  label: string;
  /** Inline step multi-chips (the two step-identity moments). */
  stepChips: boolean;
  /** step_enter's explicit ghost chip — "any step" (honest F-054 rendering). */
  anyStepGhost: boolean;
  /** E-065 part 4 — one-line document-lifecycle translation of the
   *  pipeline-mechanical label. Only where the translation is non-obvious. */
  hint?: string;
  enabled: boolean;
  disabledReason?: string;
}

export interface ScopeOption {
  scope: ScopeId;
  label: string;
  /** E-065 part 1 — how many palette actions this scope enables at the
   *  chosen moment (the same filter the THEN palette runs). 0 when disabled. */
  enabledCount: number;
  enabled: boolean;
  disabledReason?: string;
}

/** E-065 part 2 — the palette-collapse banner's data: when the current scope
 *  enables ≤2 effects and another pickable scope at this moment enables
 *  strictly more, name it and count what it unlocks. */
export interface ScopeCollapseHint {
  scope: ScopeId;
  label: string;
  /** Effects enabled at the alternative scope that are NOT enabled here. */
  unlockCount: number;
}

export interface PaletteEntry {
  key: string;
  label: string;
  description: string;
  /** Fill/transform effects link out to the field editor (different storage). */
  linkOut: boolean;
  enabled: boolean;
  disabledReason?: string;
}

/** The effective scope a ROUTE effect executes at — derived from the moment
 *  (compileMultiActionRoute ignores the authored scope; F-055's root cause). */
function routeScopeFor(moment: MomentId): ScopeId | null {
  const entry = MOMENT[moment];
  return entry ? (entry.scope as ScopeId) : null;
}

function momentHasAnyEffect(moment: MomentId): boolean {
  return EFFECTS.some((e) => e.validMoments.includes(moment));
}

/** THE (moment, scope) filter — the single predicate behind the THEN palette's
 *  enabled state, Screen 2's unlock counts, and the collapse banner (E-065:
 *  one shared function, never a re-derived copy). Route effects execute at
 *  exactly the moment-derived scope (F-055 fix). */
function effectEnabledAt(effect: (typeof EFFECTS)[number], moment: MomentId, scope: ScopeId): boolean {
  if (!effect.validMoments.includes(moment)) return false;
  if (effect.bucket === 'route') return routeScopeFor(moment) === scope;
  return effect.validScopes.includes(scope);
}

const WHERE_ROWS: Array<Omit<WhereOption, 'enabled' | 'disabledReason'>> = [
  { key: 'keying', moment: 'keying', label: 'While an operator keys a field', stepChips: false, anyStepGhost: false },
  { key: 'on_validate', moment: 'on_validate', label: 'When a document is accepted at…', stepChips: true, anyStepGhost: false },
  {
    key: 'gate', moment: 'batch_complete', label: 'When the batch is validated at the gate', stepChips: false, anyStepGhost: false,
    hint: 'Fires on the whole batch at the end-of-keying validation gate — after every document has been keyed.',
  },
  {
    key: 'step_enter', moment: 'step_enter', label: 'When a document enters…', stepChips: true, anyStepGhost: true,
    hint: 'Fires after OCR, classification, and page assembly — before an operator opens the document (for the first step) or as it moves along the pipeline.',
  },
  {
    key: 'pre_export', moment: 'pre_export', label: 'At export', stepChips: false, anyStepGhost: false,
    hint: 'Fires as the batch is released to its export destination — after keying and validation are complete.',
  },
  { key: 'batch_classified', moment: 'batch_classified', label: 'When a batch is classified', stepChips: false, anyStepGhost: false },
  // Not a runtime moment for automations (R0: no action eval site exists at
  // the batch-complete trigger; the batch's own moment is the gate row).
  { key: 'batch_completes', moment: null, label: 'When a batch completes', stepChips: false, anyStepGhost: false },
  { key: 'scheduled', moment: 'scheduled', label: 'On a schedule', stepChips: false, anyStepGhost: false },
];

export function whereOptions(): WhereOption[] {
  return WHERE_ROWS.map((row) => {
    if (row.key === 'batch_completes') {
      return {
        ...row,
        enabled: false,
        disabledReason:
          'No automation can run here yet — batch-completion has no runtime. For batch checks, use "validated at the gate".',
      };
    }
    const enabled = row.moment !== null && momentHasAnyEffect(row.moment);
    return {
      ...row,
      enabled,
      ...(enabled ? {} : { disabledReason: 'No automation can run at this moment yet.' }),
    };
  });
}

const SCOPE_LABELS: Record<ScopeId, string> = {
  field: 'A field',
  form: 'The whole document',
  batch: 'The whole batch',
};

export function scopeOptions(moment: MomentId): ScopeOption[] {
  return (['field', 'form', 'batch'] as ScopeId[]).map((scope) => {
    // E-065 part 1 — the unlock count IS the palette's enabled count (one
    // shared predicate); enabled falls out of it.
    const enabledCount = EFFECTS.filter((e) => effectEnabledAt(e, moment, scope)).length;
    const enabled = enabledCount > 0;
    return {
      scope,
      label: SCOPE_LABELS[scope],
      enabledCount,
      enabled,
      ...(enabled
        ? {}
        : {
            disabledReason: disabledScopeReason(moment, scope),
          }),
    };
  });
}

/**
 * E-065 part 2 — should the THEN palette carry a "you're in the narrow lane"
 * banner? Fires only when the current scope enables ≤2 effects AND another
 * scope at this moment enables strictly more; names the richest alternative
 * and counts the effects it would unlock (enabled there, not enabled here).
 * Registry-derived through the same predicate as the palette.
 */
export function scopeCollapseHint(moment: MomentId, scope: ScopeId): ScopeCollapseHint | null {
  const here = new Set(EFFECTS.filter((e) => effectEnabledAt(e, moment, scope)).map((e) => e.key));
  if (here.size > 2) return null;
  let best: { scope: ScopeId; enabledCount: number; unlockCount: number } | null = null;
  for (const alt of SCOPE_ORDER) {
    if (alt === scope) continue;
    const there = EFFECTS.filter((e) => effectEnabledAt(e, moment, alt));
    if (there.length <= here.size) continue; // must enable STRICTLY more
    if (!best || there.length > best.enabledCount) {
      best = {
        scope: alt,
        enabledCount: there.length,
        unlockCount: there.filter((e) => !here.has(e.key)).length,
      };
    }
  }
  return best ? { scope: best.scope, label: SCOPE_LABELS[best.scope], unlockCount: best.unlockCount } : null;
}

/**
 * E-069 — the reason a scope is unavailable, and (for the batch case) what to
 * do instead. A rule at a per-document moment runs once per document, so a
 * condition on a batch-constant value — a header field, say — resolves the
 * same way for every document in the batch: the document-scoped rule IS the
 * batch answer. Without that sentence the author reads the disabled row as
 * "this product can't do batch things" and stops.
 *
 * Derived, not per-combo copy: the teaching line is appended only when the
 * document scope is genuinely available at this moment to redirect them to.
 */
function disabledScopeReason(moment: MomentId, scope: ScopeId): string {
  const base = `Nothing can act on ${SCOPE_LABELS[scope].toLowerCase()} ${momentPhraseLower(moment)}.`;
  if (scope !== 'batch') return base;

  const documentAvailable = EFFECTS.some((e) => effectEnabledAt(e, moment, 'form'));
  if (!documentAvailable) return base;

  return `${base} Rules here act on each document as it passes — a condition on a batch-level value (a header field, say) applies to every document in the batch, so choose "${SCOPE_LABELS.form}".`;
}

function momentPhraseLower(moment: MomentId): string {
  const phrase = MOMENT_PHRASE[moment] ?? String(moment);
  return phrase.charAt(0).toLowerCase() + phrase.slice(1);
}

const SCOPE_ORDER: ScopeId[] = ['field', 'form', 'batch'];

/** "a field or the whole document or batch" — the effect's applies-to phrase,
 *  derived from its registry scopes in picker order; the repeated "the whole"
 *  collapses so document+batch reads naturally. */
function scopePhrase(scopes: ScopeId[]): string {
  return SCOPE_ORDER.filter((s) => scopes.includes(s))
    .map((s) => SCOPE_LABELS[s].toLowerCase())
    .join(' or ')
    .replace('the whole document or the whole batch', 'the whole document or batch');
}

export function effectPalette(moment: MomentId, scope: ScopeId): PaletteEntry[] {
  const routeScope = routeScopeFor(moment);
  return EFFECTS.map((e) => {
    const linkOut = e.bucket === 'fill_transform';
    const momentOk = e.validMoments.includes(moment);
    const enabled = effectEnabledAt(e, moment, scope);
    let disabledReason: string | undefined;
    if (!momentOk) {
      disabledReason = `Runs ${e.validMoments.map(momentPhraseLower).join(' or ')} — not ${momentPhraseLower(moment)}.`;
    } else if (!enabled) {
      // E-063 — a scope-filtered effect TEACHES the fix instead of just naming
      // the mismatch. Applies-to phrase: the registry's validScopes. The
      // recommended scope must be pickable on the previous screen AT THIS
      // moment, so route effects recommend the moment-derived scope (F-055 —
      // the scope they actually execute at); check effects recommend their
      // first valid scope (any of them is enabled there, since this effect
      // itself enables it).
      const recommended =
        e.bucket === 'route' && routeScope
          ? routeScope
          : SCOPE_ORDER.filter((s) => e.validScopes.includes(s))[0];
      disabledReason = recommended
        ? `${e.label} applies to ${scopePhrase(e.validScopes)} — choose '${SCOPE_LABELS[recommended]}' at the previous step.`
        : `${e.label} applies to ${scopePhrase(e.validScopes)} — not ${SCOPE_LABELS[scope].toLowerCase()}.`;
    }
    return {
      key: e.key,
      label: e.label,
      description: e.description,
      linkOut,
      enabled,
      ...(disabledReason ? { disabledReason } : {}),
    };
  });
}

/**
 * Per-effect config seed for the create flow. Wraps the shared
 * `defaultConfigForEffect` with one moment-aware exception: a webhook check
 * authored at the GATE must not carry the S-5 async opt-in keys (timeoutMs
 * would flip it into the accept-dial lane — see webhookCheck's compile in
 * @capture/shared).
 */
export function seedConfigForEffect(effectKey: string, moment: MomentId): Record<string, unknown> {
  const base = defaultConfigForEffect(effectKey);
  if (effectKey === 'webhook_check' && moment === 'batch_complete') {
    const { timeoutMs: _drop, asyncCheck: _drop2, ...rest } = base as Record<string, unknown>;
    void _drop; void _drop2;
    return rest;
  }
  // Notify seeds EMPTY (the retired tell_someone tile's contract): the role
  // picker must offer only the tenant's REAL roles — a seeded 'operator'
  // fallback would surface as a phantom option (P5 residual).
  if (effectKey === 'notify') return {};
  return base;
}

/**
 * Screen 3 stacking → branches. Route effects COMPOSE into one route branch
 * (ordered as picked); each check effect is its own branch. Fill effects never
 * reach here (linkOut). A pure-notify route branch seeds severity 'warning'
 * (informational by nature); anything else defaults to 'error' — both remain
 * editable on the effect's config form.
 */
export function seedBranchesForEffects(effectKeys: string[], moment: MomentId): AutomationBranch[] {
  const branches: AutomationBranch[] = [];
  const routeKeys = effectKeys.filter((k) => effectByKey(k)?.bucket === 'route');
  for (const key of effectKeys) {
    const effect = effectByKey(key);
    if (!effect || effect.bucket === 'fill_transform') continue;
    if (effect.bucket === 'check') {
      branches.push({ family: 'check', effectKey: key, severity: 'error', config: seedConfigForEffect(key, moment) });
    }
  }
  if (routeKeys.length > 0) {
    branches.push({
      family: 'route',
      severity: routeKeys.every((k) => k === 'notify') ? 'warning' : 'error',
      actions: routeKeys.map((k) => ({ effectKey: k, config: seedConfigForEffect(k, moment) })),
    });
  }
  return branches;
}
